import { ArrowDownTrayIcon, Cog6ToothIcon, TrashIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { type FC, type ReactNode, useState } from "react";

import type { PlaceholderListValue } from "@fronterahealth/frontera-ui-components";
import {
  Button,
  Dialog,
  FileUploadAndParse,
  Input,
  PlaceholderEditor,
  TextArea,
} from "@fronterahealth/frontera-ui-components";

import {
  PayorTypeEnum,
  type TemplateDetail as TemplateDetailType,
  useUpdateTemplateExportFile,
  useUpdateTemplateLogo,
} from "@api/rest/types-and-hooks";
import { OrganizationEhrSyncFolders } from "@components/Templates/OrganizationEhrSyncFolders";
import { notifyError } from "@components/notifications/notifications";
import {
  ExportFramework,
  PAYOR_TYPE_LABELS,
  REVIEW_SETTING_CHOICE_LABELS,
  ReportType,
  ReviewSettingChoice,
  type ReviewSettingChoiceValue,
  TemplateStatus,
  reviewSettingChoiceFromValue,
} from "@pages/Templates/constants";
import {
  useGetS3BucketTemplateExportFileUrlRetriever,
  useGetS3BucketUploadTemplateExportFileCallback,
} from "@pages/Templates/hooks/useTemplateExportFile";
import {
  useGetS3BucketTemplateLogoUrlRetriever,
  useGetS3BucketUploadTemplateLogoCallback,
} from "@pages/Templates/hooks/useTemplateLogo";
import { PlaceholderEditorVariant, PlaceholderValues } from "@pages/Templates/placeholders";
import { TEMPLATE_TYPE_LABEL, type TemplateType } from "@pages/Templates/templateType";

// Filenames from frontera-pipelines/typescript-temporal-worker/src/pdf-generation/templates/
// v2 templates are auto-selected by the worker based on workflow path — no need to expose them
const HBS_TEMPLATES = ["catalight-reassessment.hbs", "aces-catalight-reassessment.hbs"];

const EXPORT_FRAMEWORK_LABEL: Record<string, string> = {
  [ExportFramework.GOOGLE_DOCS]: "Google Docs",
  [ExportFramework.HANDLEBARS]: "Typescript",
  [ExportFramework.DOCXTEMPLATER]: "Docx Templater",
};

// --- Details Tab ---

interface DetailsTabProps {
  template: TemplateDetailType;
  isEditing: boolean;
  editName: string;
  editDescription: string;
  editStatus: string;
  editReportType: string;
  editExportFramework: string;
  editTemplateUrl: string;
  editHeaderText: string;
  editFooterText: string;
  editFont: string | null;
  editUnifiedExport: boolean;
  editTemplateType: TemplateType;
  editRequireAllQaResolved: boolean;
  editRequireApproval: ReviewSettingChoiceValue;
  editFinalApproverWaits: ReviewSettingChoiceValue;
  urlError: string;
  onChangeName: (v: string) => void;
  onChangeDescription: (v: string) => void;
  onChangeStatus: (v: string) => void;
  onChangeReportType: (v: string) => void;
  onChangeExportFramework: (v: string) => void;
  onChangeTemplateUrl: (v: string) => void;
  onChangeHeaderText: (v: string) => void;
  onChangeFooterText: (v: string) => void;
  onChangeFont: (v: string | null) => void;
  onChangeUnifiedExport: (v: boolean) => void;
  onChangeTemplateType: (v: TemplateType) => void;
  onChangeRequireAllQaResolved: (v: boolean) => void;
  onChangeRequireApproval: (v: ReviewSettingChoiceValue) => void;
  onChangeFinalApproverWaits: (v: ReviewSettingChoiceValue) => void;
  editPayorType: PayorTypeEnum | null;
  editDefaultPayorTemplate: boolean;
  onChangePayorType: (v: PayorTypeEnum | null) => void;
  onChangeDefaultPayorTemplate: (v: boolean) => void;
  onEditOrgs: () => void;
  onOrganizationFolderSaved: () => void;
  onLogoChanged: () => void;
  onExportFileUploaded: () => void;
  onExportSettingsSave: () => boolean;
}

interface ReviewSettingSelectProps {
  value: ReviewSettingChoiceValue;
  onChange: (v: ReviewSettingChoiceValue) => void;
  description: string;
}

const ReviewSettingSelect: FC<ReviewSettingSelectProps> = ({ value, onChange, description }) => (
  <div className="flex flex-col gap-1">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ReviewSettingChoiceValue)}
      className="block w-full rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
    >
      {Object.values(ReviewSettingChoice).map((choice) => (
        <option key={choice} value={choice}>
          {REVIEW_SETTING_CHOICE_LABELS[choice]}
        </option>
      ))}
    </select>
    <span className="text-xs text-text-secondary">{description}</span>
  </div>
);

export const TemplateDetails: FC<DetailsTabProps> = ({
  template,
  isEditing,
  editName,
  editDescription,
  editStatus,
  editReportType,
  editExportFramework,
  editTemplateUrl,
  editHeaderText,
  editFooterText,
  editFont,
  editUnifiedExport,
  editTemplateType,
  editRequireAllQaResolved,
  editRequireApproval,
  editFinalApproverWaits,
  urlError,
  onChangeName,
  onChangeDescription,
  onChangeStatus,
  onChangeReportType,
  onChangeExportFramework,
  onChangeTemplateUrl,
  onChangeHeaderText,
  onChangeFooterText,
  onChangeFont,
  onChangeUnifiedExport,
  onChangeTemplateType,
  onChangeRequireAllQaResolved,
  onChangeRequireApproval,
  onChangeFinalApproverWaits,
  editPayorType,
  editDefaultPayorTemplate,
  onChangePayorType,
  onChangeDefaultPayorTemplate,
  onEditOrgs,
  onOrganizationFolderSaved,
  onLogoChanged,
  onExportFileUploaded,
  onExportSettingsSave,
}) => {
  const [exportSettingsOpen, setExportSettingsOpen] = useState(false);
  const [confirmDefaultOpen, setConfirmDefaultOpen] = useState(false);

  const s3UrlRetriever = useGetS3BucketTemplateLogoUrlRetriever({ templateId: template.id });
  const s3UploadCallback = useGetS3BucketUploadTemplateLogoCallback({
    templateId: template.id,
    refetch: onLogoChanged,
  });
  const removeLogo = useUpdateTemplateLogo();

  const handleRemoveLogo = async () => {
    try {
      await removeLogo.mutateAsync({ data: { template_id: template.id, original_file_name: "" } });
      onLogoChanged();
    } catch {
      notifyError("Failed to remove logo");
    }
  };

  const s3ExportFileUrlRetriever = useGetS3BucketTemplateExportFileUrlRetriever({ templateId: template.id });
  const s3ExportFileUploadCallback = useGetS3BucketUploadTemplateExportFileCallback({
    templateId: template.id,
    refetch: onExportFileUploaded,
  });
  const removeExportFile = useUpdateTemplateExportFile();

  const handleRemoveExportFile = async () => {
    try {
      await removeExportFile.mutateAsync({ data: { template_id: template.id, original_file_name: "" } });
      onExportFileUploaded();
    } catch {
      notifyError("Failed to remove export file");
    }
  };

  // REASSESSMENT templates only support HANDLEBARS and DOCXTEMPLATER — not GOOGLE_DOCS
  const availableFrameworks: string[] =
    editReportType === ReportType.REASSESSMENT
      ? [ExportFramework.HANDLEBARS, ExportFramework.DOCXTEMPLATER]
      : Object.values(ExportFramework);

  // Dialog is always editable, so always use the draft value for framework-dependent field visibility.
  // Normalize to the first available option if the saved value is no longer valid for this report type.
  const activeFramework = availableFrameworks.includes(editExportFramework)
    ? editExportFramework
    : (availableFrameworks[0] ?? ExportFramework.HANDLEBARS);
  const showUrlField = !isEditing && !!template.template_url;

  return (
    <div>
      {/* Export Settings button — always at the top, opens the dialog */}
      <div className="flex justify-start mb-4">
        <Button
          appearance="secondary"
          text={
            <div className="flex items-center gap-1.5">
              <Cog6ToothIcon className="h-4 w-4 shrink-0" />
              <span className="text-sm">Export Settings</span>
            </div>
          }
          onClick={() => {
            if (!availableFrameworks.includes(editExportFramework)) {
              onChangeExportFramework(availableFrameworks[0] ?? ExportFramework.HANDLEBARS);
            }
            setExportSettingsOpen(true);
          }}
        />
      </div>

      <dl className="divide-y divide-border-subtle">
        <FieldRow label="ID">{String(template.id)}</FieldRow>

        <FieldRow label="Name">
          {isEditing ? (
            <Input
              name="name"
              label=""
              value={editName}
              onChange={(e) => onChangeName(e.target.value)}
              inputSize="full"
            />
          ) : (
            template.name
          )}
        </FieldRow>

        <FieldRow label="Description">
          {isEditing ? (
            <TextArea label="" value={editDescription} onChange={(e) => onChangeDescription(e.target.value)} rows={3} />
          ) : (
            <span className="whitespace-pre-wrap">{template.description ?? "—"}</span>
          )}
        </FieldRow>

        <FieldRow label="Report Type">
          {isEditing ? (
            <select
              value={editReportType}
              onChange={(e) => onChangeReportType(e.target.value)}
              className="block w-full rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            >
              {([ReportType.INITIAL_ASSESSMENT, ReportType.REASSESSMENT] as string[]).includes(
                template.report_type ?? "",
              ) ? (
                <>
                  <option value={ReportType.INITIAL_ASSESSMENT}>Initial Assessment</option>
                  <option value={ReportType.REASSESSMENT}>Reassessment</option>
                </>
              ) : (
                <>
                  <option value="">—</option>
                  <option value={ReportType.DIAGNOSIS_EVALUATION}>Diagnosis Evaluation</option>
                </>
              )}
            </select>
          ) : (
            (template.report_type ?? "—")
          )}
        </FieldRow>

        <FieldRow label="Status">
          {isEditing ? (
            <select
              value={editStatus}
              onChange={(e) => onChangeStatus(e.target.value)}
              className="block w-full rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            >
              {Object.values(TemplateStatus).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            template.status
          )}
        </FieldRow>

        {/* Export Framework — always read-only here; editable inside the Export Settings dialog */}
        <FieldRow label="Export Framework">
          {template.export_framework
            ? (EXPORT_FRAMEWORK_LABEL[template.export_framework] ?? template.export_framework)
            : "—"}
        </FieldRow>

        {showUrlField && (
          <FieldRow label="Template URL">
            <span className="break-all">{template.template_url ?? "—"}</span>
          </FieldRow>
        )}

        {/* Unified Export — read-only here; editable (Docxtemplater only) inside the Export Settings dialog */}
        <FieldRow label="Unified Export">{template.unified_export ? "Yes" : "No"}</FieldRow>

        <FieldRow label="Template Type">
          {isEditing ? (
            <div className="flex flex-col gap-1.5">
              {(["base", "enterprise", "self_service"] as const).map((t) => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="template-type"
                    value={t}
                    checked={editTemplateType === t}
                    onChange={() => onChangeTemplateType(t)}
                    className="h-4 w-4 border-border-medium text-rio-600"
                  />
                  <span className="text-sm text-text-primary">{TEMPLATE_TYPE_LABEL[t]}</span>
                </label>
              ))}
            </div>
          ) : template.is_base_template ? (
            TEMPLATE_TYPE_LABEL.base
          ) : template.enterprise_template ? (
            TEMPLATE_TYPE_LABEL.enterprise
          ) : (
            TEMPLATE_TYPE_LABEL.self_service
          )}
        </FieldRow>

        <FieldRow label="Require QA Resolution">
          {isEditing ? (
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editRequireAllQaResolved}
                onChange={(e) => onChangeRequireAllQaResolved(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border-medium text-rio-600"
              />
              <span className="text-sm text-text-primary">
                Require all active QA suggestions to be resolved
                <span className="block text-xs text-text-secondary">
                  When enabled, users cannot advance past review-and-sign until every active QA suggestion on the report
                  is resolved.
                </span>
              </span>
            </label>
          ) : template.require_all_qa_resolved ? (
            "Yes"
          ) : (
            "No"
          )}
        </FieldRow>

        <FieldRow label="Require Approval Before Export">
          {isEditing ? (
            <ReviewSettingSelect
              value={editRequireApproval}
              onChange={onChangeRequireApproval}
              description="Only enforced if the organization has Team review on. Reports cannot be completed, signed or exported until every active reviewer has approved."
            />
          ) : (
            REVIEW_SETTING_CHOICE_LABELS[reviewSettingChoiceFromValue(template.require_approval_before_export)]
          )}
        </FieldRow>

        <FieldRow label="Final Approver Waits for Reviewers">
          {isEditing ? (
            <ReviewSettingSelect
              value={editFinalApproverWaits}
              onChange={onChangeFinalApproverWaits}
              description="Only enforced if the organization has Team review on. The final approver can approve, and is notified, only after every other active reviewer has approved."
            />
          ) : (
            REVIEW_SETTING_CHOICE_LABELS[reviewSettingChoiceFromValue(template.final_approver_waits_for_reviewers)]
          )}
        </FieldRow>

        <FieldRow label="Payor Type">
          {isEditing ? (
            <select
              value={editPayorType ?? ""}
              onChange={(e) => onChangePayorType((e.target.value as PayorTypeEnum) || null)}
              className="block w-full rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            >
              <option value="">None</option>
              {Object.values(PayorTypeEnum).map((pt) => (
                <option key={pt} value={pt}>
                  {PAYOR_TYPE_LABELS[pt] ?? pt}
                </option>
              ))}
            </select>
          ) : template.payor_type ? (
            ((PAYOR_TYPE_LABELS as Record<string, string>)[template.payor_type] ?? template.payor_type)
          ) : (
            "—"
          )}
        </FieldRow>

        <FieldRow label="Default Payor Template">
          {isEditing ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editDefaultPayorTemplate}
                disabled={!editPayorType}
                onChange={(e) => {
                  if (e.target.checked) {
                    setConfirmDefaultOpen(true);
                  } else {
                    onChangeDefaultPayorTemplate(false);
                  }
                }}
                className="h-4 w-4 rounded border-border-medium text-rio-600"
              />
              <span className="text-sm text-text-primary">Default template for this payor</span>
              {editPayorType && editStatus !== TemplateStatus.PUBLISHED && (
                <span className="text-xs text-text-secondary">(takes effect when the template is published)</span>
              )}
            </label>
          ) : template.default_payor_template ? (
            "Yes"
          ) : (
            "No"
          )}
        </FieldRow>

        <FieldRow label="Created">
          {template.created_date ? format(new Date(template.created_date), "MMM d, yyyy h:mm a") : "—"}
        </FieldRow>

        <FieldRow label="Last Updated">
          {template.last_updated_date ? format(new Date(template.last_updated_date), "MMM d, yyyy h:mm a") : "—"}
        </FieldRow>
      </dl>

      <OrganizationEhrSyncFolders
        templateId={template.id}
        organizations={template.organizations ?? []}
        onManageOrganizations={onEditOrgs}
        onSaved={onOrganizationFolderSaved}
      />

      {/* PROA-986: confirm before marking this template as the payor default */}
      <Dialog
        open={confirmDefaultOpen && isEditing}
        setOpen={setConfirmDefaultOpen}
        title="Set as default template"
        size="sm"
        primaryButton={{
          text: "Make default",
          onClick: () => {
            // guard: editing may have ended, or the payor cleared, while the dialog was open
            if (isEditing && editPayorType) {
              onChangeDefaultPayorTemplate(true);
            }
            setConfirmDefaultOpen(false);
          },
        }}
        secondaryButton={{ text: "Cancel", onClick: () => setConfirmDefaultOpen(false) }}
      >
        <p className="text-sm text-text-secondary">
          Make this the default template for{" "}
          {editPayorType
            ? ((PAYOR_TYPE_LABELS as Record<string, string>)[editPayorType] ?? editPayorType)
            : "this payor"}{" "}
          / {editReportType || "this report type"}? This replaces any current default for that combination.
        </p>
      </Dialog>

      {/* Export Settings Dialog — always editable, Apply saves immediately */}
      <Dialog
        open={exportSettingsOpen}
        setOpen={setExportSettingsOpen}
        title="Export Settings"
        size="4xl"
        primaryButton={{
          text: "Apply",
          onClick: () => {
            if (onExportSettingsSave()) setExportSettingsOpen(false);
          },
        }}
        secondaryButton={{ text: "Cancel", onClick: () => setExportSettingsOpen(false) }}
      >
        <div className="space-y-5">
          {/* Export Framework — always shown */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="mb-1.5 text-sm font-medium text-text-secondary">Export Framework</p>
              <select
                value={activeFramework}
                onChange={(e) => onChangeExportFramework(e.target.value)}
                className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
              >
                {availableFrameworks.map((opt) => (
                  <option key={opt} value={opt}>
                    {EXPORT_FRAMEWORK_LABEL[opt] ?? opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Font — only relevant when Frontera generates the PDF (Handlebars) */}
            {activeFramework === ExportFramework.HANDLEBARS && (
              <div>
                <p className="mb-1.5 text-sm font-medium text-text-secondary">Font Family</p>
                <select
                  value={editFont ?? "PUBLIC_SANS"}
                  onChange={(e) => onChangeFont(e.target.value)}
                  className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
                >
                  <option value="ARIAL">Arial</option>
                  <option value="CALIBRI">Calibri</option>
                  <option value="GEORGIA">Georgia</option>
                  <option value="PUBLIC_SANS">Public Sans</option>
                  <option value="TIMES_NEW_ROMAN">Times New Roman</option>
                </select>
              </div>
            )}
          </div>

          {/* Template file — Handlebars only (.hbs selector) */}
          {activeFramework === ExportFramework.HANDLEBARS && (
            <div>
              <p className="mb-1.5 text-sm font-medium text-text-secondary">Template File</p>
              <select
                value={editTemplateUrl}
                onChange={(e) => onChangeTemplateUrl(e.target.value)}
                className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
              >
                <option value="">Default (auto-select by report type)</option>
                {HBS_TEMPLATES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Template URL — Google Docs only (text input) */}
          {activeFramework === ExportFramework.GOOGLE_DOCS && (
            <div>
              <p className="mb-1.5 text-sm font-medium text-text-secondary">Template URL</p>
              <Input
                name="template-url-dialog"
                label=""
                value={editTemplateUrl}
                onChange={(e) => onChangeTemplateUrl(e.target.value)}
                inputSize="full"
              />
              {urlError && <p className="mt-1 text-xs text-text-error-primary">{urlError}</p>}
              <p className="mt-1 text-xs text-text-tertiary">
                Paste the base Google Docs URL (without <code>/edit</code>).
              </p>
            </div>
          )}

          {/* Export file — shown whenever there is an uploaded file, or when Docxtemplater is active */}
          {(activeFramework === ExportFramework.DOCXTEMPLATER || template.template_url) && (
            <div>
              <p className="mb-1.5 text-sm font-medium text-text-secondary">Template File (.docx)</p>
              {template.template_url ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-primary truncate max-w-xs">
                    {template.template_url.split("/").pop()}
                  </span>
                  {template.template_export_file_signed_url && (
                    <a
                      href={template.template_export_file_signed_url}
                      download={template.template_url.split("/").pop()}
                      className="p-1 rounded text-text-tertiary hover:text-text-primary hover:bg-limestone-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={handleRemoveExportFile}
                    disabled={removeExportFile.isPending}
                    className="p-1 rounded text-text-error-primary hover:bg-red-50 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <FileUploadAndParse
                  key="export-file-upload-dialog"
                  fileKind=""
                  existingFiles={[]}
                  title=""
                  singleFileUpload
                  supportedFileTypes={["docx"]}
                  maxSize="100MB"
                  hintText="Upload a .docx template file"
                  S3BucketUrlRetriever={(params) => s3ExportFileUrlRetriever(params)}
                  S3BucketUploadCallback={(params) => s3ExportFileUploadCallback(params)}
                  reportFileUploadCompleteCallback={() => Promise.resolve()}
                  fileParseStatusRetriever={(_id) => Promise.resolve({ fileId: _id, status: "done" as const })}
                  removeFileCallback={() => Promise.resolve()}
                />
              )}
            </div>
          )}

          {/* Unified export — Docxtemplater only. Routes the export through the same
              server-side pipeline Handlebars templates use, instead of the legacy
              client-side renderer. Requires the .docx to use {~~field} tags. */}
          {activeFramework === ExportFramework.DOCXTEMPLATER && (
            <div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editUnifiedExport}
                  onChange={(e) => onChangeUnifiedExport(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border-medium text-rio-600"
                />
                <span className="text-sm text-text-primary">
                  Unified export
                  <span className="block text-xs text-text-secondary">
                    Renders through the same server-side pipeline Handlebars templates use, instead of the legacy
                    client-side renderer. Requires the .docx file to use <code>{"{~~field}"}</code> tags rather than{" "}
                    <code>{"{@field}"}</code> tags.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Header, Footer, Logo — only relevant when Frontera generates the PDF (Handlebars) */}
          {activeFramework === ExportFramework.HANDLEBARS && (
            <>
              <div>
                <p className="mb-1.5 text-sm font-medium text-text-secondary">Header Text</p>
                <PlaceholderEditor
                  title=""
                  className="my-1"
                  existingContent={editHeaderText}
                  hintText="Type / for dynamic content"
                  onChange={onChangeHeaderText}
                  insertValues={PlaceholderValues as PlaceholderListValue[]}
                  variant={PlaceholderEditorVariant.WYSIWYG}
                />
              </div>
              <div>
                <p className="mb-1.5 text-sm font-medium text-text-secondary">Footer Text</p>
                <PlaceholderEditor
                  title=""
                  className="my-1"
                  existingContent={editFooterText}
                  hintText="Type / for dynamic content"
                  onChange={onChangeFooterText}
                  insertValues={PlaceholderValues as PlaceholderListValue[]}
                  variant={PlaceholderEditorVariant.WYSIWYG}
                />
              </div>

              <div>
                <p className="mb-1.5 text-sm font-medium text-text-secondary">Logo</p>
                {template.template_logo_signed_url ? (
                  <div className="flex items-start gap-2">
                    <img
                      src={template.template_logo_signed_url}
                      alt="Template logo"
                      className="max-h-16 max-w-xs object-contain rounded border border-border-subtle p-1"
                    />
                    <div className="flex flex-col gap-1">
                      <a
                        href={template.template_logo_signed_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1 rounded text-text-tertiary hover:text-text-primary hover:bg-limestone-50"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </a>
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        disabled={removeLogo.isPending}
                        className="p-1 rounded text-text-error-primary hover:bg-red-50 disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <FileUploadAndParse
                    key="logo-upload-dialog"
                    fileKind=""
                    existingFiles={[]}
                    title=""
                    singleFileUpload
                    supportedFileTypes={["png", "jpg"]}
                    maxSize="100MB"
                    hintText="Recommended size: 200 × 80px"
                    S3BucketUrlRetriever={(params) => s3UrlRetriever(params)}
                    S3BucketUploadCallback={(params) => s3UploadCallback(params)}
                    reportFileUploadCompleteCallback={() => Promise.resolve()}
                    fileParseStatusRetriever={(_id) => Promise.resolve({ fileId: _id, status: "done" as const })}
                    removeFileCallback={() => Promise.resolve()}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
};

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="py-4 grid grid-cols-3 gap-4 items-start">
      <dt className="text-sm font-medium text-text-secondary">{label}</dt>
      <dd className="col-span-2 text-sm text-text-primary">{children}</dd>
    </div>
  );
}
